import { Suspense, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { RotateCw } from 'lucide-react'
import { Outlet, useLocation } from 'react-router-dom'
import { Moment, MOMENT_SLUGS, PORTAL_PAGES } from '@/constants'
import { useSessionStore } from '@/stores'
import { useIsCompact } from '@hooks/useDevice'
import { useToast } from '@/hooks/useToast'
import { ToastContainer } from '@/components/ui/ToastContainer'
import Alert from '@/components/Alert'
import { GlobalLoading } from '@components/LoadingSpinner'
import { formatCurrency } from '@utils'
import type { PortalSection, PortalSettings } from '@/types'
import OmpHeader from './sections/OmpHeader'
import OmpAnnouncementBar from './sections/OmpAnnouncementBar'
import OmpWelcomeMessage from './sections/OmpWelcomeMessage'
import OmpNavigationalBlocks from './sections/OmpNavigationalBlocks'
import OmpFooter from './sections/OmpFooter'
import OmpPaymentIssueBanner from './components/OmpPaymentIssueBanner'
import OmpPaymentUpdateAutoOpen from './components/OmpPaymentUpdateAutoOpen'
import { OmpActionsProvider } from './OmpActionContext'
import OmpActionRouter from './drawers/OmpActionRouter'
import OmpLifeline from './OmpLifeline'
import { applyOmpTheme, getThemeVariables } from './utils/themeTokens'
import {
  isFooterVisible,
  resolveAnnouncement,
  resolveMoment,
  resolveNavLabels,
  resolveSectionMeta,
  resolveWelcome,
} from './utils/sectionsConfig'
import { interpolateTemplate, readHtmlAlignment, resolveProductImage, sessionTokens } from './utils/omp'
import { isDrawerOwningFunnel } from './utils/portalSession'
import { pageFromPath } from './utils/portalNav'
import { zoneFor } from './utils/sectionZones'
import PreviewSlot from './PreviewSlot'
import { SectionKey } from '@/constants'
import { preloadImage } from '@/utils/imageCache'
import { isPreviewSession } from '@utils/session'

const OmpLayout = () => {
  const location = useLocation()
  const toast = useToast()
  const campaign = useSessionStore(s => s.campaign) as any
  const session = useSessionStore(s => s.session)
  const customer = (session as any)?.customer

  // TEMP dev-only: re-pull settings without logout. Remove before ship.
  const [refreshing, setRefreshing] = useState(false)
  const refreshSettings = async () => {
    setRefreshing(true)
    try {
      await useSessionStore.getState().getSession()
      toast.success('Settings refreshed')
    } catch {
      toast.error('Could not refresh settings')
    } finally {
      setRefreshing(false)
    }
  }

  const portalSettings = campaign?.portal_settings as PortalSettings | undefined
  const themeVariables = getThemeVariables(campaign)

  useEffect(() => {
    applyOmpTheme(themeVariables)
  }, [themeVariables])

  // Browser back must fire the session undo call (funnel parity). The browser has
  // already restored the previous URL, so we only run the server-side undo — no
  // navigation — keeping the URL (source of truth) and the session in step.
  const lastIdxRef = useRef<number>(window.history.state?.idx ?? 0)
  useEffect(() => {
    lastIdxRef.current = window.history.state?.idx ?? lastIdxRef.current
  }, [location])
  useEffect(() => {
    const onPop = async () => {
      const newIdx = window.history.state?.idx ?? 0
      const goingBack = newIdx < lastIdxRef.current
      lastIdxRef.current = newIdx
      const store = useSessionStore.getState()
      const session = store.session as any
      // Only undo for history entries that actually pushed a session step, and never
      // while a drawer owns the funnel.
      if (!goingBack || isDrawerOwningFunnel() || !session?.can_undo || !store.stepStack.length) {
        return
      }
      try {
        await store.undo(1)
      } catch {
        /* leave the browser-restored URL in place */
      }
    }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  const page = pageFromPath(location.pathname)
  const moment = resolveMoment(session)
  const momentItem = (session as any)?.moment_item
  useEffect(() => {
    if (momentItem) {
      preloadImage(resolveProductImage(momentItem))
    }
  }, [momentItem])
  const navLabels = useMemo(() => resolveNavLabels(portalSettings, page, moment), [portalSettings, page, moment])
  const announcement = useMemo(() => resolveAnnouncement(portalSettings, page, moment), [portalSettings, page, moment])
  const welcome = useMemo(() => resolveWelcome(portalSettings, page, moment), [portalSettings, page, moment])
  const showFooter = useMemo(() => isFooterVisible(portalSettings, page, moment), [portalSettings, page, moment])
  const navMeta = useMemo(
    () => resolveSectionMeta(portalSettings, SectionKey.NavigationalBlocks, page, moment),
    [portalSettings, page, moment]
  )
  const welcomeMeta = useMemo(
    () => resolveSectionMeta(portalSettings, SectionKey.WelcomeMessage, page, moment),
    [portalSettings, page, moment]
  )

  const isCompact = useIsCompact()
  const welcomeHeading = useMemo(
    () => (welcome.visible ? interpolateTemplate(welcome.data.heading, sessionTokens(session)) : ''),
    [welcome, session]
  )
  const requested = themeVariables?.navigation_blocks_position
  const navPosition = requested === 'right' || requested === 'top' ? requested : 'left'
  const isTop = navPosition === 'top'
  const credits = customer?.store_credit
  const welcomeAlign = readHtmlAlignment(welcome.data.heading)
  // /portal/account maps to the home page's config, but the welcome message is Home-only.
  const isHomePath = location.pathname === '/portal' || location.pathname === '/portal/'
  const showWelcome = isHomePath && !!welcomeHeading
  const showCredits = typeof credits === 'number' && credits > 0

  // Mobile/tablet put the welcome row and the nav inside the header (Figma 6618:71669),
  // so both surfaces render the same markup from one place.
  // CHROME_KEYS are skipped by OmpSectionRenderer, so the overlay is injected here.
  const framed = (key: SectionKey, meta: PortalSection | undefined, node: ReactNode) => (
    <PreviewSlot
      uid={meta?.uid}
      sectionKey={key}
      index={0}
      zone={zoneFor(key)}
      label={meta?.name}
      deletable={meta?.deletable}>
      {node}
    </PreviewSlot>
  )

  const welcomeRow =
    showWelcome || showCredits ? (
      <div className="omp-welcome-row" data-welcome-align={welcomeAlign}>
        {showWelcome && framed(SectionKey.WelcomeMessage, welcomeMeta, <OmpWelcomeMessage data={welcome.data} />)}
        {showCredits && <span className="omp-credits">Credits: {formatCurrency(credits)}</span>}
      </div>
    ) : null

  const navBlocks = (layout?: 'top') =>
    framed(SectionKey.NavigationalBlocks, navMeta, <OmpNavigationalBlocks labels={navLabels} layout={layout} />)

  const content = (
    <section className="omp-content-col" key={location.pathname}>
      <Suspense fallback={<div className="omp-content-loading" />}>
        <div className="animate-page-in">
          <Outlet />
        </div>
      </Suspense>
    </section>
  )

  return (
    <OmpActionsProvider>
      <div className="omp-root" data-nav-position={navPosition}>
        <ToastContainer />
        {/* Forwards warning_messages from a 200 soft-failure into the toast queue. */}
        <Alert />
        <div className="omp-header-stack">
          <OmpHeader />
          {isCompact ? (
            <div className="omp-header-mobile">
              {welcomeRow}
              {navBlocks()}
            </div>
          ) : (
            isTop && navBlocks('top')
          )}
        </div>
        {announcement &&
          announcement.is_visible !== false &&
          framed(SectionKey.AnnouncementBar, announcement, <OmpAnnouncementBar data={announcement.data} />)}
        {page === PORTAL_PAGES.Home && moment === MOMENT_SLUGS[Moment.PaymentIssue] && (
          <div className="omp-banner-wrap">
            <OmpPaymentIssueBanner />
          </div>
        )}

        <main className="omp-main">
          <div className="omp-container">
            {!isCompact && welcomeRow}

            {isTop || isCompact ? (
              content
            ) : (
              <div className="omp-layout-grid">
                <aside className="omp-nav-col">{navBlocks()}</aside>
                {content}
              </div>
            )}
          </div>
        </main>

        {showFooter && <OmpFooter />}
        <OmpActionRouter />
        <OmpPaymentUpdateAutoOpen />
        <OmpLifeline />
        <GlobalLoading />

        {/* TEMP — kept on all envs for QA; remove after QA sign-off. */}
        {!isPreviewSession(session) && (
          <button
            type="button"
            className="omp-dev-refresh"
            onClick={refreshSettings}
            disabled={refreshing}
            title="Refresh portal settings / page styles from the backend">
            <RotateCw size={16} className={refreshing ? 'omp-dev-refresh-spin' : undefined} />
            {refreshing ? 'Refreshing…' : 'Refresh settings'}
          </button>
        )}
      </div>
    </OmpActionsProvider>
  )
}

export default OmpLayout
